/**
 * Zamdan Landing Page - Luxury South Asian Fashion
 */

import React from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import {
  ArrowRight,
  Crown,
  Sparkles,
  Users,
  MessageCircle,
  BarChart3,
  ShoppingBag,
  LogIn,
} from 'lucide-react';

export default function ZamdanLanding() {
  const navigate = useNavigate();
  const forwardToZamdan = () => {
    window.location.assign('https://zamdan.com');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-pink-200/70 bg-pink-50/95 backdrop-blur-md">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="h-6 w-6 text-pink-400" />
            <span className="font-bold text-xl text-pink-700">Zamdan</span>
          </div>
          <Button
            onClick={() => navigate('/login')}
            className="gap-2 bg-pink-500 text-white font-semibold hover:bg-pink-400"
          >
            <LogIn className="h-4 w-4" />
            Projects Login
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative mt-16 bg-gradient-to-br from-pink-100 via-rose-50 to-pink-200 text-slate-900">
        <div className="container mx-auto px-4 py-20 text-center space-y-6">
          <p className="text-pink-300 text-sm uppercase tracking-widest font-semibold">
            Luxury South Asian Fashion
          </p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Timeless Heritage.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-pink-500">
              Modern Elegance.
            </span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Zamdan celebrates the richness of South Asian couture — blending
            heritage craftsmanship with contemporary luxury for weddings,
            festivities, and statement occasions worldwide.
          </p>

          <Button
            size="lg"
            onClick={forwardToZamdan}
            className="text-lg px-8 py-6 bg-pink-500 hover:bg-pink-400 text-white font-semibold"
          >
            Explore Collections <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>

        {/* Hero Image */}
        <div className="container mx-auto px-4 pb-20">
          <div className="relative max-w-5xl mx-auto">
            <ImageWithFallback
              src="https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?q=80&w=1600"
              alt="South Asian luxury fashion"
              className="rounded-lg shadow-2xl w-full h-[420px] object-cover border border-pink-200/70"
            />
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-t from-pink-300/25 via-transparent to-pink-200/25" />
          </div>
        </div>
      </header>

      {/* Brand Pillars */}
      <section className="py-20 bg-muted/40">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-muted-foreground mb-4">
            The Zamdan Experience
          </h2>
          <p className="text-xl text-muted-foreground">
            Empowering boutiques. Elevating couture.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow border hover:shadow-lg transition">
            <Sparkles className="h-10 w-10 text-pink-500 mb-4" />
            <h3 className="text-2xl font-bold mb-2">Heritage Craft</h3>
            <p className="text-muted-foreground">
              Handcrafted textiles, intricate embroidery, and generational
              artistry rooted in South Asian tradition.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow border hover:shadow-lg transition">
            <Users className="h-10 w-10 text-pink-500 mb-4" />
            <h3 className="text-2xl font-bold mb-2">Boutique Empowerment</h3>
            <p className="text-muted-foreground">
              We enable independent boutiques to launch storefronts,
              showcase collections, and connect with global customers.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow border hover:shadow-lg transition">
            <MessageCircle className="h-10 w-10 text-pink-500 mb-4" />
            <h3 className="text-2xl font-bold mb-2">Assisted Luxury</h3>
            <p className="text-muted-foreground">
              Personalized consultations via WhatsApp & Instagram ensure
              every customer finds the perfect fit and style.
            </p>
          </div>
        </div>
      </section>

      {/* Platform Capabilities */}
      <section className="py-20 bg-white">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-muted-foreground">
            Powered by Intelligent Commerce
          </h2>
          <p className="text-xl text-muted-foreground">
            Designed for modern South Asian fashion businesses
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto px-4">
          <div className="p-6 border rounded-lg hover:shadow-lg transition">
            <ShoppingBag className="h-8 w-8 text-pink-500 mb-3" />
            <h4 className="font-semibold text-lg">Boutique Storefronts</h4>
            <p className="text-sm text-muted-foreground">
              Launch mobile-first storefronts with measurement-ready product
              pages.
            </p>
          </div>

          <div className="p-6 border rounded-lg hover:shadow-lg transition">
            <MessageCircle className="h-8 w-8 text-pink-500 mb-3" />
            <h4 className="font-semibold text-lg">Assisted Selling</h4>
            <p className="text-sm text-muted-foreground">
              AI-assisted replies and reusable templates for faster customer
              engagement.
            </p>
          </div>

          <div className="p-6 border rounded-lg hover:shadow-lg transition">
            <Sparkles className="h-8 w-8 text-pink-500 mb-3" />
            <h4 className="font-semibold text-lg">Campaign Studio</h4>
            <p className="text-sm text-muted-foreground">
              Generate Instagram captions, WhatsApp broadcasts, and DM
              scripts in minutes.
            </p>
          </div>

          <div className="p-6 border rounded-lg hover:shadow-lg transition">
            <BarChart3 className="h-8 w-8 text-pink-500 mb-3" />
            <h4 className="font-semibold text-lg">Insightful Analytics</h4>
            <p className="text-sm text-muted-foreground">
              Track inquiries, engagement, and campaign performance to grow
              smarter.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-pink-200 to-pink-100 text-black text-center">
        <h2 className="text-4xl font-bold mb-6">
          Join the Future of South Asian Fashion
        </h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto">
          Whether you're a boutique owner or a couture enthusiast, Zamdan
          connects heritage craftsmanship with modern digital luxury.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            onClick={forwardToZamdan}
            className="text-lg px-8 py-6 bg-pink-600 text-white hover:bg-pink-500"
          >
            Launch Your Boutique
          </Button>

          <Button
            size="lg"
            variant="outline"
            onClick={forwardToZamdan}
            className="text-lg px-8 py-6 border-2 border-pink-600 text-pink-700 hover:bg-pink-100"
          >
            Explore Collections
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-pink-200 bg-pink-100 py-12 text-center text-pink-900">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Crown className="h-6 w-6 text-pink-400" />
          <span className="font-bold text-xl">Zamdan</span>
        </div>
        <p className="text-muted-foreground">Luxury South Asian Fashion Platform</p>
        <p className="text-sm text-muted-foreground mt-2">
          © 2026 Zamdan. All rights reserved.
        </p>
      </footer>
    </div>
  );
}